// Generic "tabbed SectionCard" primitive used in the Inventory panel
// to host a segmented control where two parallel surfaces would
// otherwise stack in their own SectionCards (Materials / Recipes on
// the left; Receive lines / Recipe count on the right). The chrome
// matches SectionCard byte-for-byte (rounded-2xl border / shadow /
// padding / dark variants) so the tabbed boxes sit cleanly next to
// other SectionCards in the same panel.
//
// Each tab carries its own optional `description` + `rightSlot` that
// renders only while that tab is active. Body for the active tab is
// passed as `children` and the parent owns whatever switch picks
// which child to render.
//
// Optional `endTabs` renders a second tab group right-justified in
// the same strip (purchase-order modes sit opposite receive/recipe count).
import { sectionCardClass } from "@/components/common/surfaceClasses";
import type { ReactNode } from "react";

export type TabbedSectionTab<T extends string> = {
  id: T;
  label: string;
  /** Optional sub-header description rendered while this tab is active. */
  description?: ReactNode;
  /** Optional right-aligned slot rendered next to the tab strip while
      this tab is active (mirrors SectionCard's `rightSlot`). */
  rightSlot?: ReactNode;
  /** Optional tone. Defaults to "default" (gray active/inactive
      states). "warning" paints the tab in amber so an attention-
      worthy tab (e.g. "Unattached" with a non-zero count) stays
      visually distinct even when inactive. */
  tone?: "default" | "warning";
};

type Props<T extends string> = {
  tabs: ReadonlyArray<TabbedSectionTab<T>>;
  activeTab: T;
  onTabChange: (id: T) => void;
  children: ReactNode;
  /** Optional extra classes (e.g. `h-full` for grid-row stretch). */
  className?: string;
  /** Slice 10: drop the rounded-2xl/border/shadow/p-4 chrome so the
   *  tabbed section can sit INSIDE another SectionCard without
   *  double-carding the layout. Tab strip + description + body
   *  still render; only the outer card is removed. */
  bare?: boolean;
  /** Optional trailing tab group, right-justified in the header row. */
  endTabs?: ReadonlyArray<TabbedSectionTab<T>>;
};

function TabStrip<T extends string>(props: {
  tabs: ReadonlyArray<TabbedSectionTab<T>>;
  activeTab: T;
  onTabChange: (id: T) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={props.ariaLabel}
      className="flex flex-nowrap overflow-x-auto max-w-full rounded-lg bg-gray-100 p-0.5 text-sm dark:bg-zinc-900"
    >
      {props.tabs.map((tab) => {
        const isActive = tab.id === props.activeTab;
        const tone = tab.tone ?? "default";
        const activeClass =
          tone === "warning"
            ? "bg-amber-100 text-amber-900 shadow-sm dark:bg-amber-900/40 dark:text-amber-100"
            : "bg-white text-gray-900 shadow-sm dark:bg-zinc-950 dark:text-zinc-100";
        const inactiveClass =
          tone === "warning"
            ? "text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
            : "text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-zinc-200";
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={
              "shrink-0 whitespace-nowrap rounded-md px-3 py-1 text-sm font-semibold transition-colors " +
              (isActive ? activeClass : inactiveClass)
            }
            onClick={() => props.onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function ManufacturingTabbedSection<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  children,
  className,
  bare = false,
  endTabs,
}: Props<T>) {
  const allTabs = endTabs?.length ? [...tabs, ...endTabs] : tabs;
  const active = allTabs.find((t) => t.id === activeTab) ?? tabs[0];
  const baseClass = bare ? "space-y-3" : sectionCardClass;
  const sectionClass = `${baseClass}${className ? ` ${className}` : ""}`;
  return (
    <section className={sectionClass}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-start justify-between gap-3">
          <TabStrip
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange}
            ariaLabel="Section view"
          />
          {endTabs?.length ? (
            <TabStrip
              tabs={endTabs}
              activeTab={activeTab}
              onTabChange={onTabChange}
              ariaLabel="Purchase order modes"
            />
          ) : null}
        </div>
        {active?.rightSlot ? <div className="shrink-0">{active.rightSlot}</div> : null}
      </div>
      {active?.description ? (
        <p className="text-xs text-gray-600 dark:text-zinc-300">{active.description}</p>
      ) : null}
      {children}
    </section>
  );
}
